import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setMobile } from "../../../redux/slices/authSlice";
import "../../../styles/globals.scss";
import InputField from "../../../components/InputField";
import AuthCard from "../../../components/AuthCard";
import Button from "../../../components/Button";
import RequiredLabel from "../../../components/RequiredLabel";
import { showNotification } from "../../../redux/slices/notificationSlice";
import ErrorText from "../../../components/ErrorText";

import {
  OTP_TYPE,
  sendOtp,
  redirectToGoogleLogin,
  redirectToAppleLogin,
  enableAppleLogin,
} from "./services/authServices";

export default function SignInMobile() {
  // Store raw digits (no spaces); allow leading 0 while typing
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { t } = useTranslation();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const urlError = params.get("error");

  const errorMessages = {
    email_exists: t('emailAlreadyRegistered'),
    google_failed: t('googleLoginFailed'),
    google_fetch: t('googleFetchFailed'),
  };
  const rawPhone = (phoneNumber || "").replace(/\D/g, "");
  const normalizedPhone = rawPhone.replace(/^0+/, "");
  const isPhoneValid = normalizedPhone.length === 9 && /^4\d{8}$/.test(normalizedPhone);
  const requiredReason = t("pleaseCompleteRequiredFields");

  // 123 456 789 formatting for readability
  const formatTriads = (digits) => {
    const clean = (digits || "").replace(/\D/g, "");
    if (!clean) return "";
    return clean.replace(/(\d{3})(?=\d)/g, "$1 ");
  };

  const handleContinue = async () => {
    if (isSubmitting) return;
    const raw = (phoneNumber || "").replace(/\D/g, "");
    const normalized = raw.replace(/^0+/, "");
    if (!/^[0-9]+$/.test(raw)) {
      setError(t('invalidPhone'));
      return;
    }
    if (normalized.length !== 9 || !/^4\d{8}$/.test(normalized)) {
      setError(t('invalidPhone'));
      return;
    }

    setError("");
    setIsSubmitting(true);
    dispatch(setMobile(normalized));

    try {
      await sendOtp(`+61${normalized}`, OTP_TYPE.PHONE);
      dispatch(showNotification({ message: t('otpSent'), type: "success" }));
      navigate("/auth/verify-mobile-number");
    } catch (err) {
      setError(t('sendOtpFail'));
      dispatch(
        showNotification({ message: t('sendOtpFail'), type: "error" })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    let v = digits;
    if (v.length > 9) {
      if (v.startsWith("0")) v = v.slice(1);
      v = v.slice(0, 9);
    }
    setPhoneNumber(v);
    if (error) setError("");
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-50 animate-fade-in">
      <AuthCard>
        <h2 className="main-header mb-4 text-center">{t("signIn")}</h2>
        <RequiredLabel htmlFor="signin-mobile" className="w-full mb-2">
          {t("mobileNumber")}
        </RequiredLabel>
        <div className="flex gap-2 w-full mb-4">
          <input
            type="text"
            className="w-12 h-12 border rounded-lg px-2 text-sm bg-white text-center"
            placeholder="+61"
            disabled
          />
          <InputField
            id="signin-mobile"
            type="tel"
            className="flex-1 h-12 text-base placeholder:text-base"
            placeholder={t("phoneNumberPlaceholder")}
            value={formatTriads(phoneNumber)}
            onChange={handlePhoneChange}
            required
          />
        </div>
        {error && <ErrorText>{error}</ErrorText>}
        <Button
          className="w-full mb-2"
          onClick={handleContinue}
          disabled={!isPhoneValid || isSubmitting}
          disabledReason={!isPhoneValid ? requiredReason : ""}
          loading={isSubmitting}
          loadingLabel={t("loading")}
        >
          {t("continueWithMobile")}
        </Button>
        <div className="mt-4 text-sm text-gray-500 text-center">
          {t("orSignInWith")}
        </div>
        <div className="flex flex-col gap-3 justify-center mt-4 w-full">
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => navigate("/auth/sign-in-with-email")}
          >
            {t("email")}
          </Button>
          {enableAppleLogin && (
            <Button
              variant="secondary"
              className="w-full"
              onClick={redirectToAppleLogin}
            >
              {t("apple")}
            </Button>
          )}
          <Button
            variant="secondary"
            className="w-full"
            onClick={redirectToGoogleLogin}
          >
            {t("google")}
          </Button>
        </div>
        {urlError && (
          <ErrorText>
            {errorMessages[urlError] || t("loginWentWrong")}
          </ErrorText>
        )}

        <hr className="mt-4" />
        <div className="mt-4 text-sm text-gray-500 text-center">
          {t("dontHaveAccount")}
        </div>

        <div className="flex gap-3 justify-center mt-4 w-full">
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => navigate("/auth/sign-up-with-email")}
          >
            {t("createAccount")}
          </Button>
        </div>
      </AuthCard>
    </div>
  );
}
